import { fetchPredictions, fetchRadarPredictions } from '@sg/garnish';

import { getEnvVars } from '@order/utils';

import { ActiveCartDeliveryDetailsDocument } from '../../graphql/ActiveCartDeliveryDetails.generated';
import {
  AddDeliveryAddressDocument,
  AddDeliveryAddressV2Document,
} from '../../graphql/AddDeliveryAddress.generated';
import { CustomerAddressesDocument } from '../../graphql/CustomerDeliveryDetails.generated';
import {
  DeliveryLocationByAddressIdDocument,
  type DeliveryLocationByAddressIdQuery,
  type DeliveryLocationByAddressIdQueryVariables,
  type DeliveryLocationByAddressQuery,
  type DeliveryLocationByAddressQueryVariables,
  DeliveryLocationByAddressV2Document,
  type DeliveryLocationByAddressV2Query,
  type DeliveryLocationByAddressV2QueryVariables,
  DeliveryLocationByAddressWithDisclosureFieldsDocument,
  type DeliveryLocationByAddressWithDisclosureFieldsQuery,
} from '../../graphql/DeliveryLocationByAddress.generated';
import { DeliveryLocationByAddressDocument } from '../../graphql/DeliveryLocationByAddress.generated';

import type { ActiveCartDeliveryDetailsQuery } from '../../graphql/ActiveCartDeliveryDetails.generated';
import type {
  AddDeliveryAddressMutation,
  AddDeliveryAddressMutationVariables,
  AddDeliveryAddressV2Mutation,
  AddDeliveryAddressV2MutationVariables,
} from '../../graphql/AddDeliveryAddress.generated';
import type { CustomerAddressesQuery } from '../../graphql/CustomerDeliveryDetails.generated';
import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Queries delivery location details based on provided address identifiers
 */
export async function deliveryLocationByAddressQuery(
  client: Client,
  variables: DeliveryLocationByAddressQueryVariables,
) {
  return client
    .query<DeliveryLocationByAddressQuery>(DeliveryLocationByAddressDocument, variables, {
      requestPolicy: 'network-only',
    })
    .toPromise();
}

/**
 * Queries delivery location details (+ extra disclosure fields) based on provided address identifiers
 */
export async function deliveryLocationByAddressWithDisclosureFieldsQuery(
  client: Client,
  variables: DeliveryLocationByAddressQueryVariables,
) {
  return client
    .query<DeliveryLocationByAddressWithDisclosureFieldsQuery>(
      DeliveryLocationByAddressWithDisclosureFieldsDocument,
      variables,
      { requestPolicy: 'network-only' },
    )
    .toPromise();
}

/**
 * Queries delivery location details based on provided address identifiers (V2 - coordinate-based, no Google Place ID)
 */
export async function deliveryLocationByAddressV2Query(
  client: Client,
  variables: DeliveryLocationByAddressV2QueryVariables,
) {
  return client
    .query<DeliveryLocationByAddressV2Query>(DeliveryLocationByAddressV2Document, variables, {
      requestPolicy: 'network-only',
    })
    .toPromise();
}

/**
 * Queries delivery location details based on provided address id
 */
export async function deliveryLocationByAddressIdQuery(
  client: Client,
  variables: DeliveryLocationByAddressIdQueryVariables,
) {
  return client
    .query<DeliveryLocationByAddressIdQuery>(DeliveryLocationByAddressIdDocument, variables, {
      requestPolicy: 'network-only',
    })
    .toPromise();
}

/**
 * Queries active cart delivery details
 */
export async function activeCartDeliveryDetailsQuery(client: Client) {
  return client
    .query<ActiveCartDeliveryDetailsQuery>(
      ActiveCartDeliveryDetailsDocument,
      {},
      { requestPolicy: 'cache-first' },
    )
    .toPromise();
}

/**
 * Adds new delivery address for the current customer
 */
export async function addDeliveryAddress(
  client: Client,
  variables: AddDeliveryAddressMutationVariables,
) {
  return client
    .mutation<AddDeliveryAddressMutation>(AddDeliveryAddressDocument, variables, {
      requestPolicy: 'cache-and-network',
    })
    .toPromise();
}

/**
 * Adds new delivery address for the current customer (V2 - no Google Place ID required)
 */
export async function addDeliveryAddressV2(
  client: Client,
  variables: AddDeliveryAddressV2MutationVariables,
) {
  return client
    .mutation<AddDeliveryAddressV2Mutation>(AddDeliveryAddressV2Document, variables, {
      requestPolicy: 'cache-and-network',
    })
    .toPromise();
}

/**
 * Queries current customer delivery addresses
 */
export async function customerAddressesQuery(client: Client) {
  return client
    .query<CustomerAddressesQuery>(CustomerAddressesDocument, {}, { requestPolicy: 'cache-first' })
    .toPromise();
}

/**
 * Queries delivery address predictions based on the provided search string
 */
export async function addressPredictionsQuery(searchString: string) {
  const { GOOGLE_API_KEY } = getEnvVars();

  return fetchPredictions({ input: searchString, apiKey: GOOGLE_API_KEY });
}

/**
 * Queries delivery address predictions based on the provided search string
 */
export async function radarAddressPredictionsQuery(searchString: string, near?: string) {
  const { RADAR_SDK_KEY } = getEnvVars();

  return fetchRadarPredictions({
    query: searchString,
    apiKey: RADAR_SDK_KEY,
    near,
  });
}
